import { canPreviewName } from '../formats/extension-rules';
import type { DocumentFormat, DocumentFormatSelectedEvent, ExtensionMap, UnknownFileBehavior } from '../models/viewer-types';
import { createViewerSelect } from './viewer-select';

/** One format a reader may ask for: the value the viewer routes on, and the text that names it. */
export type OpenFormatOption = [value: Exclude<DocumentFormat, 'auto'>, text: string];

/** What the reader may pick, whether the viewer failed to tell the format or was never asked to. */
export const supportedOpenFormats: Array<OpenFormatOption> = [
  ['xlsx', 'Excel (XLSX)'],
  ['docx', 'Word (DOCX)'],
  ['pdf', 'PDF'],
  ['image', 'Image'],
  ['markdown', 'Markdown'],
  ['json', 'JSON'],
  ['xml', 'XML'],
  ['html', 'HTML'],
  ['code', 'Code'],
  ['text', 'Text'],
  ['msp360-log', 'MSP360 log'],
  ['zip', 'ZIP archive'],
  ['msp360-log-archive', 'MSP360 log archive']
];

export interface ChooseFormatOptions {
  behavior?: UnknownFileBehavior;
  allowArchive?: boolean;
  signal?: AbortSignal;
  onSelect?: (event: DocumentFormatSelectedEvent) => void;
}

const archiveFormats = ['zip', 'msp360-log-archive'];

let dialogId = 0;

/**
 * The extension the viewer has nothing to say about.
 *
 * @param {string} name Name of the document.
 * @param {ExtensionMap} [map] Associations the host application has set.
 * @param {DocumentFormat} [format] A format the caller insists on, which answers for the name.
 * @return {string} The extension, with its dot, or an empty string when the name is understood.
 */
export function unknownExtension(name: string, map: ExtensionMap = {}, format: DocumentFormat = 'auto'): string {
  if (format !== 'auto' || canPreviewName(name, map)) return '';

  return /\.[^./\\]+$/.exec(name.split(/[\\/]/).pop() as string)?.[0] || '';
}

/**
 * Asks the reader what to open an unknown file as. The answer comes before any bytes are fetched, and
 * aborting closes the dialog this function owns and answers with a cancellation.
 *
 * @param {HTMLElement} root Where the dialog is placed.
 * @param {string} name Name of the document.
 * @param {string} extension The extension nobody claims.
 * @param {ChooseFormatOptions} [options] What to do, and how to be told the answer.
 * @return {Promise<string>} The chosen format, `auto` when detection was asked for, null on cancel.
 */
export function chooseUnknownFormat(
  root: HTMLElement,
  name: string,
  extension: string,
  options: ChooseFormatOptions = {}
): Promise<string | null> {
  const { behavior = 'prompt', allowArchive = false, signal, onSelect } = options;

  if (!extension || behavior === 'detect') return Promise.resolve('auto');

  if (behavior !== 'prompt') {
    throw Error(`File type ${extension} is not supported. Select a supported format or configure a file association.`);
  }

  if (signal?.aborted) return Promise.resolve(null);

  const document = root.ownerDocument;
  const dialog = document.createElement('dialog');
  const title = document.createElement('h2');
  const description = document.createElement('p');
  const form = document.createElement('form');
  const label = document.createElement('div');
  const actions = document.createElement('div');
  const cancel = document.createElement('button');
  const open = document.createElement('button');

  dialog.setAttribute('part', 'viewer-dialog');
  dialog.className = 'unknown-format-dialog';

  title.setAttribute('part', 'viewer-dialog-title');
  title.id = `unknown-format-${++dialogId}`;
  title.textContent = 'Unsupported file type';
  dialog.setAttribute('aria-labelledby', title.id);

  description.setAttribute('part', 'viewer-dialog-description');
  description.textContent =
    `${name}: ${extension} is not supported. Choose a format to try opening it, or cancel. ` + 'This does not convert the file.';

  label.textContent = 'Open as';

  const select = createViewerSelect(document, {
    label: 'Open as',
    options: supportedOpenFormats.filter(([value]) => allowArchive || !archiveFormats.includes(value)),
    onChange: (value: string): void => {
      open.disabled = !value;
    }
  });

  label.append(select.element);

  cancel.setAttribute('part', 'viewer-dialog-cancel');
  cancel.type = 'button';
  cancel.textContent = 'Cancel';

  open.setAttribute('part', 'viewer-dialog-open');
  open.type = 'submit';
  open.textContent = 'Open';
  open.disabled = true;

  actions.setAttribute('part', 'viewer-dialog-actions');
  actions.append(cancel, open);
  form.append(label, actions);
  dialog.append(title, description, form);

  return new Promise<string | null>((resolve) => {
    let finished = false;

    const finish = (value: string | null): void => {
      if (finished) return;

      finished = true;
      signal?.removeEventListener('abort', abort);
      dialog.close();
      dialog.remove();
      resolve(value);
    };

    const abort = (): void => finish(null);

    cancel.onclick = (): void => finish(null);
    dialog.oncancel = (event: Event): void => {
      event.preventDefault();
      finish(null);
    };
    dialog.onclose = (): void => finish(null);
    form.onsubmit = (event: SubmitEvent): void => {
      event.preventDefault();

      if (finished || !select.value) return;

      const format = select.value;

      finish(format);
      onSelect?.({
        fileName: name,
        extension: extension.toLowerCase(),
        format: format as DocumentFormatSelectedEvent['format']
      });
    };

    signal?.addEventListener('abort', abort, { once: true });
    root.append(dialog);
    dialog.showModal();
    select.focus();
  });
}
